import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { IPermitTypeField, IRichText, SourceEntityType } from '~/client/graph'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import RichEditorParsed from '../../../RichTextEditor/components/RichEditorParsed'
import RichTextEditor from '../../../RichTextEditor/RichTextEditor'
import StruxhubInputLabel from '../../../StruxhubInputs/components/StruxhubInputLabel'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'

const getDefaultValue = (): IRichText => ({
  content: '',
  attachments: [],
})

interface IProps {
  typeField: IPermitTypeField

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore
  workflowTypesStore?: WorkflowTypesStore

  isViewMode: boolean

  parentInstanceId?: string
  parentValueIndex?: number
}

@inject('workflowTypesStore')
@observer
export default class PermitFormRichTextField extends React.Component<IProps> {
  public render(): JSX.Element {
    const {
      isViewMode,
      typeField,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
      workflowTypesStore,
    } = this.props
    const mainLocation = store.editablePermit.locations[0]

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={null}
        viewModeRenderer={this.renderViewMode}
        onAddFieldClick={this.addNewFieldValue}
      >
        {/* Matches the top margin BaseStruxhubInput gives every other field's
            label; this field renders StruxhubInputLabel directly. */}
        <div className="tw:mt-2">
          <StruxhubInputLabel
            label={typeField.caption}
            isRequired={typeField.isMandatory}
            value={this.fieldValues[0]?.content || ''}
            isValid
          />
        </div>
        {this.fieldValues.map((value, index) => {
          const additionalInfo = {
            sourceEntityId: store.editablePermit.id,
            sourceEntityType: SourceEntityType.Form,
            locationId: mainLocation?.id,
            sourceEntityCode: store.editablePermit.code,
            sourceEntityTypeName:
              store.editablePermit.getNameOfPermitType(workflowTypesStore),
            locationName: mainLocation?.id
              ? store.getLocationName(mainLocation)
              : undefined,
          }
          return (
            <div key={`${typeField.id}_${index}`} className="my12">
              <RichTextEditor
                value={value}
                onChange={v => this.onChange(v, index)}
                additionalInfo={additionalInfo}
                addToUpdateLogList={store.addToUpdateLogList}
              />
            </div>
          )
        })}
      </PermitBaseFormField>
    )
  }

  private renderViewMode = (value: IRichText): JSX.Element => {
    if (value?.content?.length || value?.attachments?.length) {
      return (
        <RichEditorParsed
          value={value}
          isContentExpanded
          className="mt10 bb-light-grey"
        />
      )
    }
    return <div className="text large word-break-all">{NO_VALUE}</div>
  }

  private get fieldValues(): IRichText[] {
    const { typeField, fieldsStore, parentInstanceId, parentValueIndex } =
      this.props
    const values = fieldsStore.getFieldValues<IRichText>(
      typeField.id,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : [getDefaultValue()]
  }

  private onChange = (value: IRichText, index: number) => {
    this.props.fieldsStore.changeFieldValue(
      this.props.typeField,
      index,
      value,
      this.props.parentInstanceId,
      this.props.parentValueIndex,
    )
  }

  private addNewFieldValue = () => {
    const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
      this.props

    fieldsStore.addNewFieldValue(
      typeField,
      getDefaultValue(),
      parentInstanceId,
      parentValueIndex,
    )
  }
}
